import React, {
	useEffect,
	useMemo,
	useRef,
	useState,
} from 'react';
import classNames from 'classnames';
import { useSelector } from 'react-redux';
import Icon, { COLORS, ICONS } from '../general-components/icon';
import Popover from '../general-components/popover';
import { ZendeskCategory, ZendeskSection, SolutionCenterViews } from '../../types/solution-center';
import { GreenroomState as GS } from '../../stores/types/types';

interface BreadcrumbsProps {
	loadingContent: boolean;
	currentCategory: ZendeskCategory | undefined;
	currentSection: ZendeskSection | undefined;
	handleSetHomeView: () => void;
	handleSelectCategory: (categoryId?: number) => void;
	handleSelectSection: (sectionId?: number) => void;
}

export default function Breadcrumbs(props: BreadcrumbsProps) {
	const {
		loadingContent,
		currentCategory,
		currentSection,
		handleSetHomeView,
		handleSelectCategory,
		handleSelectSection,
	} = props;

	const view = useSelector((s: GS) => s.UserInterfaceReducer.solutionCenterView);

	const [breadcrumbRefWidth, setBreadcrumbRefWidth] = useState<number>(0);
	const [hiddenBreadcrumbCount, setHiddenBreadcrumbCount] = useState<number>(0);
	const [breadcrumbPopoverOpen, setBreadcrumbPopoverOpen] = useState<boolean>(false);

	const breadcrumbRef = useRef<HTMLDivElement>(null);

	const getHiddenBreadcrumbs = () => {
		if (breadcrumbRef.current) {
			// reset the hidden breadcrumb count
			let hiddenBreadcrumbLength = 0;
			setHiddenBreadcrumbCount(0);

			// determine if the breadcrumb container is overflowing and by how much
			let excessWidth = breadcrumbRef.current.scrollWidth - breadcrumbRef.current.clientWidth;
			if (excessWidth > 0) excessWidth += 16; // account for ellipsis button

			breadcrumbRef.current.childNodes.forEach((child) => {
				if (!breadcrumbRef.current || !(child instanceof HTMLElement) || excessWidth <= 0) return;

				// to determine if still overflowing after removing the child
				excessWidth -= child.clientWidth;

				// the last child will still display the element with text-overflow set to ellipsis
				if (child !== breadcrumbRef.current.lastChild) {
					hiddenBreadcrumbLength += 1;
				}
			});

			setHiddenBreadcrumbCount(hiddenBreadcrumbLength);
		}
	};

	const resizeObserver = new ResizeObserver((entries) => {
		if (entries[0].contentRect.width && entries[0].contentRect.width !== breadcrumbRefWidth) {
			setBreadcrumbRefWidth(entries[0].contentRect.width);
			getHiddenBreadcrumbs();
		}
	});

	useEffect(() => {
		if (breadcrumbRef.current) {
			resizeObserver.observe(breadcrumbRef.current);
		}

		return () => {
			if (breadcrumbRef.current) {
				resizeObserver.disconnect();
			}
		};
	}, [breadcrumbRef, resizeObserver]);

	const breadcrumbs = useMemo(() => [
		{
			name: 'Solution Center',
			onClick: () => handleSetHomeView(),
			associatedView: SolutionCenterViews.Home,
		},
		...(currentCategory ? [{
			name: currentCategory?.name,
			onClick: () => handleSelectCategory(),
			associatedView: SolutionCenterViews.Category,
		}] : []),
		...(currentSection ? [{
			name: currentSection?.name,
			onClick: () => handleSelectSection(),
			associatedView: SolutionCenterViews.Section,
		}] : []),
	], [currentCategory, currentSection]);

	return (
		<>
			<div
				ref={breadcrumbRef}
				className={classNames('sc-breadcrumb-wrapper', loadingContent && 'loading-content')}
			>
				{hiddenBreadcrumbCount > 0 && (
					<button
						className="sc-breadcrumb-hidden-btn"
						onClick={() => setBreadcrumbPopoverOpen(!breadcrumbPopoverOpen)}
					>
						<Icon
							name={ICONS.THREE_DOTS_HORIZONTAL}
							size={10}
							color={COLORS.TEXT_GRAY_100}
						/>
					</button>
				)}
				{breadcrumbs.map((b, i) => (
					<div
						key={`breadcrumb-${i}`}
						style={{ display: hiddenBreadcrumbCount > i ? 'none' : 'flex' }}
						className={classNames(
							'sc-breadcrumb-container',
							hiddenBreadcrumbCount === i
							&& view === SolutionCenterViews.Home
							&& 'hide-overflow',
						)}
					>
						{i !== 0 && (
							<span className="sc-c2-text caret">&nbsp;&gt;&nbsp;</span>
						)}
						<span
							className={classNames('sc-c2-text', view !== b.associatedView && 'can-click')}
							onClick={() => b.onClick()}
						>
							{b.name}
						</span>
					</div>
				))}
			</div>
			<Popover
				open={breadcrumbPopoverOpen}
				close={() => setBreadcrumbPopoverOpen(false)}
				className="hidden-breadcrumbs"
			>
				<ul className="sc-list breadcrumb-popover">
					{Array.from({ length: hiddenBreadcrumbCount }, (_, i) => (
						<li
							key={`hidden-breadcrumb-${i}`}
							className="sc-breadcrumb-popover-item"
							onClick={() => {
								breadcrumbs[i].onClick();
								setBreadcrumbPopoverOpen(false);
							}}
						>
							<span className="sc-c2-text breadcrumb-popover">
								{breadcrumbs[i].name}
							</span>
						</li>
					))}
				</ul>
			</Popover>
		</>
	);
}
